/**
 * useListViewMode — JEDEN mechanizm przełącznika trybu widoku w nagłówku stron list silnika
 * (kartoteki + klasy dokumentów renderowane przez `ConfigurableTable` w trybie sterowanym).
 *
 * Kontrakt:
 *  - strona trzyma tryb (`viewMode`, start 'list') i przekazuje go tabeli razem z handlerem
 *    zmiany — tabela sama podnosi zapisany `defaultViewMode` layoutu przez `onViewModeChange`
 *    i sama zapisuje zmianę trybu do layoutu;
 *  - tabela ZGŁASZA stronie tryby dostępne w bieżącym layoucie (`onAvailableViewModesChange` —
 *    m.in. kalendarz tylko przy `calendarConfig`), a hook buduje z nich pastylki przełącznika.
 *    Dzięki temu pastylka „Kalendarz" pojawia się wyłącznie na listach z kalendarzem, a przy
 *    domyślnym trybie `calendar` jest aktywna od razu po załadowaniu;
 *  - aktywna pastylka = tryb strony sklampowany do dostępnych (pierwszy dostępny, gdy tryb
 *    wypadł z layoutu) — ta sama reguła, którą tabela stosuje do renderu, więc pastylka i widok
 *    się nie rozjeżdżają.
 *
 * Użycie: `<ListViewModeToggle {...toggleProps} testIdPrefix="…" />` w nagłówku oraz
 * `<ConfigurableTable {...tableProps} … />`. Przed pierwszym raportem tabeli (np. przy stanie
 * błędu katalogu pól, gdy tabela się nie montuje) przełącznik pokazuje Lista / Karty / Kanban.
 */

import { useCallback, useMemo, useState } from 'react';
import type { ViewMode } from '@saas/shared';

import {
  DEFAULT_LIST_VIEW_MODES,
  listViewModeOptions,
  type ViewModeOption,
} from './ListViewModeToggle';

export interface ListViewModeToggleBinding {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  modes: ViewModeOption[];
}

export interface ListViewModeTableBinding {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onAvailableViewModesChange: (modes: ViewMode[]) => void;
}

export interface ListViewModeBinding {
  /** Propsy przełącznika w nagłówku strony (`ListViewModeToggle`). */
  toggleProps: ListViewModeToggleBinding;
  /** Propsy tabeli (`ConfigurableTable`) — tryb sterowany + raport dostępnych trybów. */
  tableProps: ListViewModeTableBinding;
}

function sameModes(a: readonly ViewMode[], b: readonly ViewMode[]): boolean {
  return a.length === b.length && a.every((mode, i) => mode === b[i]);
}

export function useListViewMode(): ListViewModeBinding {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [availableModes, setAvailableModes] =
    useState<readonly ViewMode[]>(DEFAULT_LIST_VIEW_MODES);

  const handleAvailableViewModesChange = useCallback((modes: ViewMode[]) => {
    setAvailableModes((prev) => (sameModes(prev, modes) ? prev : modes));
  }, []);

  const activeMode = availableModes.includes(viewMode) ? viewMode : (availableModes[0] ?? viewMode);
  const modes = useMemo(() => listViewModeOptions(availableModes), [availableModes]);

  return useMemo<ListViewModeBinding>(
    () => ({
      toggleProps: { value: activeMode, onChange: setViewMode, modes },
      tableProps: {
        viewMode,
        onViewModeChange: setViewMode,
        onAvailableViewModesChange: handleAvailableViewModesChange,
      },
    }),
    [activeMode, modes, viewMode, handleAvailableViewModesChange],
  );
}
